// Regulatory Framework (Greek RES + storage, Category 11A / 11B) section.
// Rendered only when the revenue model is Merchant / Trading (Arbitrage).
// Selecting 11A automatically locks grid charging OFF and restricts the
// Market Revenue Stack to DAM (handled by the parent's onChange handler).
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MaybeTooltip from "./MaybeTooltip";
import {
  REGULATORY_FRAMEWORK_MODES,
  REGULATORY_FRAMEWORK_HELPER_TEXT,
  REGULATORY_MODE_DESCRIPTIONS,
  INJECTION_CAP_INPUT_MODE_OPTIONS,
  INJECTION_CAP_BASIS_OPTIONS,
  INJECTION_CAP_HELPER_TEXT,
  REFERENCE_PRICE_INDEXATION_OPTIONS,
  validateRegulatoryFramework,
} from "./config/regulatoryFramework";
import { useLanguage } from "../context/LanguageContext";

const HOURS = Array.from({ length: 24 }, (_, h) => h);

function hourLabel(h) {
  return `${String(h).padStart(2, "0")}:00`;
}

function resolveBasisKw(basis, connectionCapacityKw, pvInstalledKw, bessPowerKw) {
  if (basis === "pv_installed") return Math.max(0, Number(pvInstalledKw) || 0);
  if (basis === "pv_plus_bess")
    return Math.max(0, Number(pvInstalledKw) || 0) + Math.max(0, Number(bessPowerKw) || 0);
  return Math.max(0, Number(connectionCapacityKw) || 0);
}

function HourSelect({ label, value, onChange, disabled }) {
  return (
    <FormControl size="small" sx={{ minWidth: 110 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))}>
        {HOURS.map((h) => (
          <MenuItem key={h} value={h}>
            {hourLabel(h)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function RegulatoryFrameworkSection({
  value,
  onChange,
  connectionCapacityKw,
  pvInstalledKw,
  bessPowerKw,
  isStandaloneBess,
}) {
  // Both captions below carry a kW figure inside the sentence, so the text-node
  // translator has no static node to match. Written out per language here.
  const { isGreek } = useLanguage();
  const rf = value;
  const validation = validateRegulatoryFramework(rf);
  const is11A = rf.mode === "11A";
  const is11B = rf.mode === "11B";
  const isActive = is11A || is11B;
  const cap = rf.injectionCap || {};
  const capWindows = cap.windows || [];
  const basisKw = resolveBasisKw(cap.basis, connectionCapacityKw, pvInstalledKw, bessPowerKw);

  const patch = (partial) => onChange({ ...rf, ...partial });
  const patchCap = (partial) => patch({ injectionCap: { ...cap, ...partial } });
  const patchCapWindow = (idx, partial) =>
    patchCap({
      windows: capWindows.map((w, i) => (i === idx ? { ...w, ...partial } : w)),
    });

  const infoIcon = (title) => (
    <InputAdornment position="end">
      <MaybeTooltip title={title} placement="top" arrow>
        <IconButton size="small" tabIndex={-1}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </MaybeTooltip>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isActive ? "success.main" : "divider",
        backgroundColor: "background.default",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Regulatory Framework — RES + Storage (Greece, Category 11A / 11B)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {REGULATORY_FRAMEWORK_HELPER_TEXT}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: is11A ? "2fr 1fr" : "2fr" },
          gap: 2,
        }}
      >
        <FormControl fullWidth>
          <InputLabel>Regulatory framework</InputLabel>
          <Select
            label="Regulatory framework"
            value={rf.mode}
            onChange={(e) => patch({ mode: e.target.value })}
          >
            {REGULATORY_FRAMEWORK_MODES.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.value === "11A" && Boolean(isStandaloneBess)}
              >
                {option.label}
                {option.value === "11A" && isStandaloneBess ? " — requires a PV unit" : ""}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{REGULATORY_MODE_DESCRIPTIONS[rf.mode] || ""}</FormHelperText>
        </FormControl>

        {is11A && (
          <TextField
            fullWidth
            label="PV Reference Price (EUR/MWh)"
            type="number"
            value={rf.pvReferencePriceEurMwh ?? ""}
            onChange={(e) =>
              patch({
                pvReferencePriceEurMwh: e.target.value === "" ? "" : Number(e.target.value),
              })
            }
            error={Boolean(validation.pvReferencePriceEurMwh)}
            helperText={
              validation.pvReferencePriceEurMwh ||
              "Fixed price paid for PV energy injected at the POI, constant for the whole year."
            }
            inputProps={{ min: 0, step: "any" }}
            InputProps={{
              endAdornment: infoIcon(
                "Τιμή Αναφοράς: the fixed, predictable price at which PV energy is remunerated under Category 11A. Battery energy is remunerated at the DAM price."
              ),
            }}
          />
        )}

        {is11A && (
          <FormControl fullWidth>
            <InputLabel id="ref-price-indexation-label">Reference Price over the horizon</InputLabel>
            <Select
              labelId="ref-price-indexation-label"
              label="Reference Price over the horizon"
              value={rf.referencePriceIndexation || "flat_nominal"}
              onChange={(e) => patch({ referencePriceIndexation: e.target.value })}
            >
              {REFERENCE_PRICE_INDEXATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {(
                REFERENCE_PRICE_INDEXATION_OPTIONS.find(
                  (option) => option.value === (rf.referencePriceIndexation || "flat_nominal")
                ) || {}
              ).description || ""}
            </FormHelperText>
          </FormControl>
        )}
      </Box>

      <Accordion
        elevation={0}
        disableGutters
        sx={{ mt: 1, backgroundColor: "transparent", "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="body2">Learn more — how 11A and 11B differ</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              p: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              How injected energy is remunerated
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>11A — Per-source remuneration:</b> two separate revenue logics. PV energy is
              paid a fixed, predictable Reference Price (Τιμή Αναφοράς) for the whole year,
              while battery energy is paid the variable DAM price.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <b>11B — Single market remuneration:</b> one dynamic revenue stream. PV and
              battery energy are both paid at the DAM price.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Where the battery may charge from
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>11A — PV only:</b> the battery charges only from PV surplus or curtailed PV
              energy. No charging from the grid (the Enable Grid Charging toggle is locked OFF).
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <b>11B — Flexible charging (PV &amp; grid):</b> the battery may charge from the PV
              or from the grid — more arbitrage opportunities.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Injection limits at the POI
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>11A — Fixed cap:</b> a static daily injection-cap profile, e.g. 60% of the
              connection capacity during 07:00-19:00 and no cap for the remaining hours.
            </Typography>
            <Typography variant="body2">
              <b>11B — Market rules / dynamic caps:</b> the injection limit varies by hour of
              the day (e.g. 0% until 09:00, then 70% / 35% windows), configured below as
              multiple daily windows.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {is11A && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Category 11A automatically enforces: battery charging from PV only (grid charging
          locked OFF), Market Revenue Stack restricted to DAM, PV settled at the Reference
          Price and battery settled at DAM in the optimizer and the bankability report.
        </Alert>
      )}
      {is11B && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          Category 11B keeps DAM market settlement for all exported energy and your grid
          charging preference, and enforces the time-varying injection-cap profile below.
        </Alert>
      )}

      {isActive && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(cap.enabled)}
                onChange={(e) => patchCap({ enabled: e.target.checked })}
                size="small"
              />
            }
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Injection-cap profile at the POI
                {is11A ? " (static daily profile)" : " (time-varying daily profile)"}
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {INJECTION_CAP_HELPER_TEXT}
          </Typography>
          {cap.enabled && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
                  gap: 2,
                  mb: 1.5,
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Cap input mode</InputLabel>
                  <Select
                    label="Cap input mode"
                    value={cap.inputMode || "percent_of_basis"}
                    onChange={(e) => patchCap({ inputMode: e.target.value })}
                  >
                    {INJECTION_CAP_INPUT_MODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {cap.inputMode !== "absolute_kw" && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Capacity basis</InputLabel>
                    <Select
                      label="Capacity basis"
                      value={cap.basis || "connection_capacity"}
                      onChange={(e) => patchCap({ basis: e.target.value })}
                    >
                      {INJECTION_CAP_BASIS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {`${isGreek ? "Τρέχουσα βάση" : "Current basis"}: ${basisKw.toLocaleString(
                        "en-US",
                        { maximumFractionDigits: 0 }
                      )} kW`}
                    </FormHelperText>
                  </FormControl>
                )}
              </Box>
              {capWindows.map((w, idx) => {
                const capValueNum = Number(w.capValue) || 0;
                const effectiveKw =
                  cap.inputMode === "absolute_kw" ? capValueNum : (capValueNum / 100) * basisKw;
                return (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}
                  >
                    <HourSelect
                      label="Start hour"
                      value={w.startHour}
                      onChange={(v) => patchCapWindow(idx, { startHour: v })}
                    />
                    <HourSelect
                      label="End hour (incl.)"
                      value={w.endHour}
                      onChange={(v) => patchCapWindow(idx, { endHour: v })}
                    />
                    <TextField
                      size="small"
                      sx={{ width: 170 }}
                      label={cap.inputMode === "absolute_kw" ? "Cap (kW)" : "Cap (% of basis)"}
                      type="number"
                      value={w.capValue ?? ""}
                      onChange={(e) =>
                        patchCapWindow(idx, {
                          capValue: e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      inputProps={{ min: 0, step: "any" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {`= ${effectiveKw.toLocaleString("en-US", { maximumFractionDigits: 0 })} kW ${
                        isGreek ? "μέγιστη έγχυση" : "max injection"
                      }`}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => patchCap({ windows: capWindows.filter((_, i) => i !== idx) })}
                      disabled={capWindows.length <= 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  patchCap({ windows: [...capWindows, { startHour: 0, endHour: 23, capValue: 100 }] })
                }
              >
                Add window
              </Button>
              {validation.injectionCap && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {validation.injectionCap}
                </Alert>
              )}
              <FormHelperText sx={{ mt: 0.5 }}>
                Hours outside every window fall back to the contracted connection capacity (grid
                export limit), which is enforced 24/7. Overlapping windows apply the most
                conservative (lowest) cap. Windows may wrap midnight.
              </FormHelperText>
            </>
          )}
        </Box>
      )}

      {/* Daily dispatch windows moved to the BESS page (DispatchWindowsSection):
          they are battery operating constraints, not regulatory parameters.
          The config keys stay under arbitrageMerchant.regulatoryFramework. */}
    </Box>
  );
}
